import React, {useEffect, useState} from 'react';
import {BanknotesIcon, PencilSquareIcon, PlusCircleIcon, TrashIcon, PaperClipIcon} from '@heroicons/react/24/outline';
import {Dialog, DialogPanel, DialogTitle} from '@headlessui/react';
import {formatCurrency, formatDate} from '../../utils';
import {getFileUrl} from '../../utils/api';
import ConfirmationDialog from './ConfirmationDialog';
import DateSelector from './DateSelector';
import FileUploader from './FileUploader';

function PaymentModal({
                          isOpen,
                          onClose,
                          payment,
                          payments,
                          onSave,
                          title = "Payment Details",
                          shortName,
                          isCreatingNew
                      }) {
    // Initialize state from passed payment or defaults using local timezone for date
    const today = new Date();
    const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    const [formData, setFormData] = useState({
        number: '',
        amount: '',
        description: '',
        date: localDate,
        payee: '',
        files: []
    });

    // Track original files (for edit operations) to identify removed files
    const [originalFiles, setOriginalFiles] = useState([]);
    // Track files that were removed during this edit session
    const [removedFiles, setRemovedFiles] = useState([]);
    
    // Update form data when payment prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (payment) {
            // Fix date timezone issue by preserving exactly what came from the server
            // without any Date conversion which can cause timezone issues
            let dateValue = payment.date;
            if (dateValue && typeof dateValue === 'string') {
                // Ensure we get YYYY-MM-DD format without timezone issues
                // Just take the date part (before T or just the whole string if no T)
                dateValue = dateValue.split('T')[0];
            }
            
            // Save original files for tracking removed files
            const paymentFiles = payment.files || [];
            setOriginalFiles(paymentFiles.map(file => ({...file})));
            
            // Reset removed files when opening a new payment
            setRemovedFiles([]);

            setFormData({
                ...payment,
                // Ensure we have the right data types
                id: payment.id, // Preserve the ID
                number: payment.number || '',
                amount: payment.amount || '',
                description: payment.description || '',
                date: dateValue || localDate,
                payee: payment.payee || '',
                files: paymentFiles
            });
        } else {
            // For new payments, calculate the next payment number
            let nextNumber = 1;
            if (Array.isArray(payments) && payments.length > 0) {
                const existingNumbers = payments.map(p => parseInt(p.number) || 0);
                nextNumber = Math.max(...existingNumbers) + 1;
            }
            
            // Reset form for new payments with auto-generated payment number
            const today = new Date();
            const localDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            
            setFormData({
                number: nextNumber,
                amount: '',
                description: '',
                date: localDate,
                payee: '',
                files: []
            });
            setOriginalFiles([]);
            setRemovedFiles([]);
        }
    }, [payment, payments, isOpen]);

    const [error, setError] = useState('');

    const handleInputChange = (e) => {
        const {name, value} = e.target;
        setFormData({
            ...formData,
            [name]: name === 'amount' || name === 'number' ?
                (value === '' ? '' : Number(value)) :
                value
        });
    };

    // Handle file changes, track removed files
    const handleFilesChange = (newFiles) => {
        // Identify which files were removed in this operation
        const currentFileIds = formData.files.map(file => file.uuid);
        const newFileIds = newFiles.map(file => file.uuid);
        
        // Find files that were in currentFileIds but not in newFileIds
        const justRemovedFileIds = currentFileIds.filter(id => !newFileIds.includes(id));
        
        // Add to removed files if they were part of the original files
        const originalFileIds = originalFiles.map(file => file.uuid);
        const removedOriginalFiles = justRemovedFileIds.filter(id => originalFileIds.includes(id));
        
        if (removedOriginalFiles.length > 0) {
            setRemovedFiles(prev => [...prev, ...removedOriginalFiles]);
        }
        
        // Update form data with new files
        setFormData({
            ...formData,
            files: newFiles
        });
    };
    
    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate the form
        if (!formData.amount) {
            setError('Amount is required');
            return;
        }

        if (isNaN(Number(formData.amount))) {
            setError('Amount must be a valid number');
            return;
        }

        // Clear any errors
        setError('');
        
        // Ensure ID is preserved if this is an edit operation
        const dataToSave = {...formData};
        if (payment && payment.id) {
            dataToSave.id = payment.id;
            console.log("Preserving payment ID:", payment.id);
        }
        
        // Add the list of files that need to be deleted on the server
        if (removedFiles.length > 0) {
            dataToSave.filesToDelete = removedFiles;
        }

        // Call the save function
        onSave(dataToSave);

        // Close the modal
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={onClose} className="relative z-10">
            <div
                className="fixed inset-0 bg-gray-500 bg-opacity-75 dark:bg-gray-900 dark:bg-opacity-75 transition-opacity"/>

            <div className="fixed inset-0 z-10 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <DialogPanel
                        className="relative transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                        <div className="absolute top-0 right-0 pt-4 pr-4">
                            <button
                                type="button"
                                className="rounded-md bg-white dark:bg-gray-800 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                                onClick={onClose}
                            >
                                <span className="sr-only">Close</span>
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                     strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12"/>
                                </svg>
                            </button>
                        </div>

                        <div>
                            <div className="sm:mt-5">
                                <DialogTitle as="h2"
                                             className="text-base/7 font-semibold text-gray-900 dark:text-gray-100">
                                    {title}
                                </DialogTitle>

                                {error && (
                                    <div
                                        className="mt-2 rounded-md bg-red-50 dark:bg-red-900/50 p-4 text-sm text-red-700 dark:text-red-300">
                                        {error}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit} className="mt-6">
                                    <div className="space-y-6">
                                        <div>
                                            <label htmlFor="number"
                                                   className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                                                Payment #
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    type="number"
                                                    name="number"
                                                    id="number"
                                                    value={formData.number}
                                                    onChange={handleInputChange}
                                                    className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                    placeholder="Payment number"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="amount"
                                                   className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                                                Amount (required)
                                            </label>
                                            <div className="mt-2">
                                                <div className="relative">
                                                    <input
                                                        type="number"
                                                        name="amount"
                                                        id="amount"
                                                        value={formData.amount}
                                                        onChange={handleInputChange}
                                                        className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 pl-8 pr-12 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                        placeholder="0.00"
                                                        required
                                                    />
                                                    <div
                                                        className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                        <span
                                                            className="text-gray-500 dark:text-gray-400 sm:text-sm/6">$</span>
                                                    </div>
                                                    <div
                                                        className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                                                        <span
                                                            className="text-gray-500 dark:text-gray-400 sm:text-sm/6">USD</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="description"
                                                   className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                                                Description
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    type="text"
                                                    name="description"
                                                    id="description"
                                                    value={formData.description}
                                                    onChange={handleInputChange}
                                                    className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                    placeholder="Payment description"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="payee"
                                                   className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                                                Payee
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    type="text"
                                                    name="payee"
                                                    id="payee"
                                                    value={formData.payee}
                                                    onChange={handleInputChange}
                                                    className="block w-full rounded-md bg-white dark:bg-gray-700 px-3 py-1.5 text-base text-gray-900 dark:text-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 outline-none placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm/6"
                                                    placeholder="Payee (defaults to subcontractor)"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="date"
                                                   className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                                                Date
                                            </label>
                                            <div className="mt-2">
                                                <DateSelector
                                                  selectedDate={formData.date}
                                                  onChange={(date) => setFormData({...formData, date})}
                                                  className="mt-1"
                                                />
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <label className="block text-sm/6 font-medium text-gray-900 dark:text-gray-300">
                                                Attachments
                                            </label>
                                            <div className="mt-2">
                                                <FileUploader
                                                    files={formData.files || []}
                                                    onChange={handleFilesChange}
                                                    multiple={true}
                                                    isReadOnly={false}
                                                    allowedFileTypes="image/png, image/jpeg, image/gif, application/pdf"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-8 flex items-center justify-end gap-x-6">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="text-sm/6 font-semibold text-gray-900 dark:text-gray-100"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            className="rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                        >
                                            Save
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </div>
        </Dialog>
    );
}

function PaymentsList({payments = [], shortName, onRefresh}) {
    // Ensure payments is always an array
    const paymentsList = Array.isArray(payments) ? payments : [];
    
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isCreatingNew, setIsCreatingNew] = useState(false);

    // Import API functions lazily to avoid circular dependencies
    const handleViewPayment = (payment) => {
        console.log("View payment:", payment);
        setSelectedPayment({...payment}); // Create a copy to avoid reference issues
        setIsCreatingNew(false);
        setShowModal(true);
    };

    const handleAddPayment = () => {
        setSelectedPayment(null);
        setIsCreatingNew(true);
        setShowModal(true);
        console.log("Opening payment modal", showModal); // Debug log
    };

    const handleDeleteClick = (e, payment) => {
        e.stopPropagation(); // Prevent triggering the view payment action
        setSelectedPayment(payment);
        setShowDeleteConfirm(true);
    };

    const handleSavePayment = async (paymentData) => {
        try {
            console.log("Saving payment with data:", paymentData);
            
            // Ensure files array exists
            if (!paymentData.files) {
                paymentData.files = [];
            }
            
            // Handle files that need to be deleted on the server (from existing payments)
            const filesToDelete = paymentData.filesToDelete || [];
            
            if (isCreatingNew) {
                // Add new payment
                console.log("Creating new payment");
                const {addScopePayment} = await import('../../utils/api');
                await addScopePayment(shortName, paymentData);
            } else {
                // Update existing payment
                console.log("Updating existing payment");
                console.log("Payment ID:", paymentData.id);
                console.log("Selected payment:", selectedPayment);
                
                // Handle missing ID by using the ID from selectedPayment
                if (!paymentData.id && selectedPayment && selectedPayment.id) {
                    console.log("Payment missing ID, using ID from selectedPayment:", selectedPayment.id);
                    paymentData.id = selectedPayment.id;
                }
                
                if (!paymentData.id) {
                    console.error("Payment is missing ID and cannot be determined:", paymentData);
                    throw new Error("Payment ID is missing, cannot update payment");
                }
                
                const {updateScopePayment, deleteFile} = await import('../../utils/api');
                
                // First, update the payment in the database
                const paymentId = paymentData.id;
                
                // Remove the filesToDelete field from the data before sending to the API
                const paymentDataToSave = {...paymentData};
                delete paymentDataToSave.filesToDelete;
                
                await updateScopePayment(shortName, paymentId, paymentDataToSave);
                console.log("Payment updated successfully with ID:", paymentId);
                
                // Then, if there are files to delete, delete them one by one
                if (filesToDelete.length > 0) {
                    console.log("Deleting files:", filesToDelete);
                    for (const fileUuid of filesToDelete) {
                        try {
                            await deleteFile(fileUuid);
                            console.log(`Deleted file ${fileUuid}`);
                        } catch (fileErr) {
                            console.error(`Error deleting file ${fileUuid}:`, fileErr);
                            // Continue with other files even if one fails
                        }
                    }
                }
            }

            // Refresh the data
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error saving payment:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const handleDeletePayment = async () => {
        try {
            const {deleteScopePayment} = await import('../../utils/api');
            await deleteScopePayment(shortName, selectedPayment.id);

            // Close the confirmation dialog
            setShowDeleteConfirm(false);

            // Refresh the data
            if (onRefresh) {
                onRefresh();
            }
        } catch (error) {
            console.error('Error deleting payment:', error);
            alert(`Error: ${error.message}`);
        }
    };

    const hasNoPayments = paymentsList.length === 0;

    // Prepare the JSX for just the add button
    const noPaymentsContent = (
        <div>
            <button
                onClick={handleAddPayment}
                className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
            >
                <PlusCircleIcon className="h-5 w-5 mr-1"/>
                Add payment
            </button>
        </div>
    );

    // Always return the same structure but conditionally render the list
    return (
        <div>
            {hasNoPayments ? (
                noPaymentsContent
            ) : (
                <>
                    <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
                        {paymentsList.map((payment, index) => (
                            <li
                                key={payment.id || index}
                                className="flex items-center justify-between py-4 pr-5 pl-4 text-sm hover:bg-gray-50 cursor-pointer"
                                onClick={() => handleViewPayment(payment)}
                            >
                                <div className="flex w-0 flex-1 items-center">
                                    <BanknotesIcon className="h-5 w-5 shrink-0 text-gray-400" aria-hidden="true"/>
                                    <div className="ml-4 flex min-w-0 flex-1 gap-2">
                                        <span className="truncate font-medium">
                                          Payment #{payment.number} - {payment.description || 'Payment'}
                                          {payment.payee && (
                                            <span className="ml-2 text-sm text-gray-500">
                                              to {payment.payee}
                                            </span>
                                          )}
                                          {payment.files && payment.files.length > 0 && (
                                            <span className="ml-1 inline-flex">
                                              <PaperClipIcon className="h-4 w-4 text-gray-500 translate-y-[1px]" aria-hidden="true" />
                                              <span className="ml-0.5 text-xs text-gray-500 translate-y-[2px]">{payment.files.length}</span>
                                            </span>
                                          )}
                                        </span>
                                        <span className="shrink-0 text-gray-400">{formatDate(payment.date)}</span>
                                    </div>
                                </div>
                                <div className="ml-4 flex items-center space-x-4">
                                    <div className="flex-shrink-0 font-medium">
                                        {formatCurrency(payment.amount)}
                                    </div>
                                    <div className="flex space-x-1">
                                        <button
                                            className="text-gray-400 hover:text-indigo-600"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleViewPayment(payment);
                                            }}
                                            title="Edit payment"
                                        >
                                            <PencilSquareIcon className="h-5 w-5"/>
                                        </button>
                                        <button
                                            className="text-gray-400 hover:text-red-600"
                                            onClick={(e) => handleDeleteClick(e, payment)}
                                            title="Delete payment"
                                        >
                                            <TrashIcon className="h-5 w-5"/>
                                        </button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-3">
                        <button
                            onClick={handleAddPayment}
                            className="inline-flex items-center text-sm text-indigo-600 hover:text-indigo-500"
                        >
                            <PlusCircleIcon className="h-5 w-5 mr-1"/>
                            Add payment
                        </button>
                    </div>
                </>
            )}

            {/* Payment form modal - Always include this */}
            <PaymentModal
                isOpen={showModal}
                onClose={() => {
                    console.log("Closing payment modal");
                    setShowModal(false);
                }}
                payment={isCreatingNew ? null : selectedPayment}
                payments={paymentsList}
                onSave={handleSavePayment}
                title={isCreatingNew ? "Add New Payment" : "Edit Payment"}
                shortName={shortName}
                isCreatingNew={isCreatingNew}
            />

            {/* Delete confirmation dialog */}
            <ConfirmationDialog
                open={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                title="Delete Payment"
                message={`Are you sure you want to delete Payment #${selectedPayment?.number}? This action cannot be undone.`}
                onConfirm={handleDeletePayment}
                onCancel={() => setShowDeleteConfirm(false)}
                confirmButtonText="Delete"
                confirmButtonClass="bg-red-600 hover:bg-red-500"
                icon="warning"
            />
        </div>
    );
}

export default PaymentsList;